import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApiClientCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  ApiClientCreatedDto,
  CreateApiClientDto,
} from "@/features/admin/api/dto";

/**
 * Crée un client API partenaire (`POST /ApiClients/add` — endpoint gateway,
 * §6.1). ⚠️ La `apiKey` n'est retournée QUE dans cette réponse 201 : la
 * surface d'appel doit l'afficher immédiatement à l'opérateur.
 */
export const useCreateApiClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "api-clients", "create"],
    mutationFn: (body: CreateApiClientDto) =>
      mutateData({
        url: adminEndpoints.apiClients.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<ApiClientCreatedDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, adminApiClientCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateApiClient failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
